import { useCallback } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import {
  revalidateChronologyData,
  revalidateChronologyEvents,
  revalidateChronologyVersions,
} from '@/hooks/chronology-cache';
import type { ChronologyTagGroupInfo, ChronologyTagInfo } from '@/hooks/useChronologies';
import type { ChronologyEventTag } from '@/hooks/useChronology';
import { backendClient } from '@/lib/api-client';

/** Invalidate the SWR cache for an event's audit history so a mounted
 *  AuditPanel re-fetches after the event was just mutated. Used by every
 *  edit op below — saves callers from threading their own mutate. */
function eventHistoryKey(matterGuid: string, eventGuid: string): string {
  return `/matters/${matterGuid}/chronology-events/${eventGuid}/versions`;
}

function eventDetailKey(matterGuid: string, eventGuid: string): string {
  return `/matters/${matterGuid}/chronology-events/${eventGuid}`;
}

function invalidateEventHistory(matterGuid: string, eventGuid: string): Promise<unknown> {
  return globalMutate(eventHistoryKey(matterGuid, eventGuid));
}

function isEventDetailKey(key: unknown, matterGuid: string, eventGuid: string): key is string {
  if (typeof key !== 'string') return false;
  const baseKey = eventDetailKey(matterGuid, eventGuid);
  return key === baseKey || key.startsWith(`${baseKey}?`);
}

function revalidateEventDetail(matterGuid: string, eventGuid: string): Promise<unknown> {
  return globalMutate((key) => isEventDetailKey(key, matterGuid, eventGuid));
}

function invalidateEventCaches(matterGuid: string, eventGuid: string): Promise<unknown[]> {
  return Promise.all([
    invalidateEventHistory(matterGuid, eventGuid),
    revalidateEventDetail(matterGuid, eventGuid),
  ]);
}

function revalidateEventCachesInBackground(matterGuid: string, eventGuid: string) {
  void invalidateEventCaches(matterGuid, eventGuid).catch((err) => {
    console.error('Failed to revalidate chronology event caches', err);
  });
}

function revalidateChronologyVersionsInBackground(matterGuid: string, chronologyId?: string) {
  void revalidateChronologyVersions(matterGuid, chronologyId).catch((err) => {
    console.error('Failed to revalidate chronology history', err);
  });
}

function revalidateChronologyEventsInBackground(matterGuid: string, chronologyId?: string) {
  void revalidateChronologyEvents(matterGuid, chronologyId).catch((err) => {
    console.error('Failed to revalidate chronology events', err);
  });
}

function isChronologyListKey(key: unknown): key is string {
  return typeof key === 'string'
    && /^\/matters\/[^/]+\/(?:chronology-events|chronologies\/[^/]+\/events)$/.test(key);
}

type CachedEvent = {
  id?: string;
  tags?: ChronologyEventTag[];
  description?: string | null;
  detail?: string | null;
  status?: string;
  source?: 'generated' | 'manual';
  is_locked?: boolean;
};

type ChronologyListCache = {
  events?: CachedEvent[];
};

type EventDetailCache = {
  event?: CachedEvent;
};

function patchCachedEvent(matterGuid: string, eventGuid: string, patch: Partial<EventEditState>) {
  const applyPatch = <T extends CachedEvent>(event: T): T => event?.id === eventGuid
    ? {
        ...event,
        tags: patch.tags ?? event.tags,
        description: patch.description ?? event.description,
        detail: patch.detail ?? event.detail,
        status: patch.status ?? event.status,
        source: patch.source ?? event.source,
        is_locked: patch.is_locked ?? event.is_locked,
      } as T
    : event;

  globalMutate(
    isChronologyListKey,
    (data: ChronologyListCache | undefined) => data?.events
      ? { ...data, events: data.events.map(applyPatch) }
      : data,
    { revalidate: false },
  );
  globalMutate(
    (key) => isEventDetailKey(key, matterGuid, eventGuid),
    (data: EventDetailCache | undefined) => data?.event
      ? { ...data, event: applyPatch(data.event) }
      : data,
    { revalidate: false },
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ManualEdits {
  schema_version?: number;
  edited_fields?: string[];
  removed_proposition_ids?: string[];
  removed_doc_event_ids?: string[];
  removed_entity_ids?: string[];
  created_via?: 'manual';
  legacy_lock?: boolean;
  edited_at?: string;
  edited_by_user_id?: string;
}

export interface EventEditState {
  id: string;
  chronology_id: string;
  tags: ChronologyEventTag[];
  description: string | null;
  detail: string | null;
  temporal_expression: string | null;
  status: string;
  source: 'generated' | 'manual';
  is_locked: boolean;
  deleted_at: string | null;
  restored_at: string | null;
  manual_edits: ManualEdits;
  version_id?: string;
}

export interface AffectedEvent {
  id: string;
  description: string | null;
  temporal_display: string | null;
}

export interface ChronologyVersionRow {
  id: string;
  version_number: number;
  trigger: 'rebuild' | 'user_edit' | 'doc_added' | 'doc_deleted' | null;
  edited_by_user_id: number | null;
  edited_by_user_name: string | null;
  edit_event_id: string | null;
  affected_event: AffectedEvent | null;
  edit_payload: Record<string, unknown> | null;
  delta_payload: Record<string, unknown> | null;
  created_at: string | null;
}

export interface VersionsResponse {
  versions: ChronologyVersionRow[];
  next_cursor: string | null;
  reconstruction_floor: { earliest_reconstructable_version_number: number | null };
}

export interface EventHistoryRow {
  id: string;
  version_number: number;
  trigger: string | null;
  edited_by_user_id: number | null;
  edited_by_user_name: string | null;
  edit_payload: Record<string, unknown> | null;
  delta_payload: Record<string, unknown> | null;
  created_at: string | null;
}

export interface EventHistoryResponse {
  versions: EventHistoryRow[];
}

export interface PatchEventBody {
  description?: string;
  detail?: string;
  temporal_expression?: string;
  add_entity_ids?: string[];
  remove_entity_ids?: string[];
  add_proposition_ids?: string[];
  remove_proposition_ids?: string[];
  add_doc_event_ids?: string[];
  remove_doc_event_ids?: string[];
  tag_ids?: string[];
}

export interface ManualCreateBody {
  chronology_id: string;
  description: string;
  detail?: string;
  temporal_expression: string;
  tag_ids?: string[];
  proposition_ids?: string[];
  doc_event_ids?: string[];
  entity_ids?: string[];
}

export interface DocumentSearchResult {
  document_id: number;
  file_name: string;
  doc_type: string | null;
  doc_date: string | null;
  author?: string | null;
  /** Lexicographically-first proposition of the doc, used as the
   *  attachment anchor while the picker is doc-level only. Segment-level
   *  selection will surface real proposition_ids in a future UI mode. */
  rep_proposition_id: string;
}

/** User-meaningful source-level diff returned by the restore-event dry-run.
 *  Projection events surface sources directly OR via trunk lineage fan-out
 *  depending on whether they've been edited; this shape collapses both
 *  representations into a single set, so the dialog reflects what the user
 *  sees rather than the underlying junction-table churn. */
export interface SourceMeta {
  id: string;
  description: string | null;
  doc_filename: string | null;
}

export interface EntityMeta {
  id: string;
  name: string;
}

export interface ChronologyConfigChanges {
  edited_fields: string[];
  prev_values: Record<string, unknown>;
  new_values: Record<string, unknown>;
}

export interface RestoreDryRunResponse {
  events_to_bring_back_count: number;
  events_to_remove_count: number;
  events_to_keep_count: number;
  events_to_update_count: number;
  config_changes?: ChronologyConfigChanges;
  tombstones_differ?: boolean;
}

export interface SourceChanges {
  props: { removed: SourceMeta[]; added_back: SourceMeta[] };
  doc_events: { removed: SourceMeta[]; added_back: SourceMeta[] };
  entities: { removed: EntityMeta[]; added_back: EntityMeta[] };
}

export interface EventRestoreDryRunResponse {
  target_version_number: number;
  edited_fields: string[];
  prev_values: Record<string, unknown>;
  new_values: Record<string, unknown>;
  target_deleted: boolean;
  currently_deleted: boolean;
  source_changes: SourceChanges;
}

// ---------------------------------------------------------------------------
// Hooks: edit operations
// ---------------------------------------------------------------------------

export function useEventEditing(matterGuid: string | undefined) {
  const patchEvent = useCallback(
    async (
      eventGuid: string,
      body: PatchEventBody,
      opts: { idempotencyKey?: string } = {},
    ): Promise<EventEditState> => {
      if (!matterGuid) throw new Error('matterGuid required');
      const headers: Record<string, string> = {};
      if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
      const response = await backendClient.patch(
        `/matters/${matterGuid}/chronology-events/${eventGuid}`,
        body,
        { headers },
      );
      patchCachedEvent(matterGuid, eventGuid, response.data);
      revalidateEventCachesInBackground(matterGuid, eventGuid);
      revalidateChronologyVersionsInBackground(matterGuid, response.data?.chronology_id);
      revalidateChronologyEventsInBackground(matterGuid, response.data?.chronology_id);
      return response.data;
    },
    [matterGuid],
  );

  const softDeleteEvent = useCallback(async (eventGuid: string) => {
    if (!matterGuid) throw new Error('matterGuid required');
    const response = await backendClient.delete(
      `/matters/${matterGuid}/chronology-events/${eventGuid}`,
    );
    await Promise.all([
      invalidateEventCaches(matterGuid, eventGuid),
      revalidateChronologyData(matterGuid),
    ]);
    return response.data as { deleted: boolean; version_id: string };
  }, [matterGuid]);

  const undeleteEvent = useCallback(async (eventGuid: string) => {
    if (!matterGuid) throw new Error('matterGuid required');
    const response = await backendClient.post(
      `/matters/${matterGuid}/chronology-events/${eventGuid}/restorations`,
      { mode: 'undelete' },
    );
    await Promise.all([
      invalidateEventCaches(matterGuid, eventGuid),
      revalidateChronologyData(matterGuid, response.data?.chronology_id),
    ]);
    return response.data as EventEditState;
  }, [matterGuid]);

  const revertEvent = useCallback(async (eventGuid: string) => {
    if (!matterGuid) throw new Error('matterGuid required');
    const response = await backendClient.post(
      `/matters/${matterGuid}/chronology-events/${eventGuid}/reversions`,
    );
    await Promise.all([
      invalidateEventCaches(matterGuid, eventGuid),
      revalidateChronologyData(matterGuid, response.data?.chronology_id),
    ]);
    return response.data as EventEditState;
  }, [matterGuid]);

  const restoreEventToVersion = useCallback(
    async (eventGuid: string, versionId: string) => {
      if (!matterGuid) throw new Error('matterGuid required');
      const response = await backendClient.post(
        `/matters/${matterGuid}/chronology-events/${eventGuid}/restorations`,
        { version_id: versionId },
      );
      await Promise.all([
        invalidateEventCaches(matterGuid, eventGuid),
        revalidateChronologyData(matterGuid, response.data?.chronology_id),
      ]);
      return response.data as EventEditState;
    },
    [matterGuid],
  );

  const dryRunEventRestore = useCallback(
    async (eventGuid: string, versionId: string) => {
      if (!matterGuid) throw new Error('matterGuid required');
      const response = await backendClient.post(
        `/matters/${matterGuid}/chronology-events/${eventGuid}/restorations`,
        { version_id: versionId, dry_run: true },
      );
      return response.data as EventRestoreDryRunResponse;
    },
    [matterGuid],
  );

  // Manual create is scoped by matter + chronology on the backend. The body's
  // chronology_id is promoted to the path; the rest forwards as the payload.
  const createManualEvent = useCallback(async (body: ManualCreateBody) => {
    if (!matterGuid) throw new Error('matterGuid required');
    const { chronology_id, ...rest } = body;
    const response = await backendClient.post(
      `/matters/${matterGuid}/chronologies/${chronology_id}/events`,
      rest,
    );
    await revalidateChronologyData(matterGuid, chronology_id);
    return response.data as EventEditState;
  }, [matterGuid]);

  return {
    patchEvent,
    softDeleteEvent,
    undeleteEvent,
    revertEvent,
    restoreEventToVersion,
    dryRunEventRestore,
    createManualEvent,
  };
}

// ---------------------------------------------------------------------------
// Hooks: tags + config
// ---------------------------------------------------------------------------

export function useChronologyTags(matterGuid: string | undefined, chronologyId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<{
    tag_groups: ChronologyTagGroupInfo[];
    tags: ChronologyTagInfo[];
  }>(
    matterGuid && chronologyId
      ? `/matters/${matterGuid}/chronologies/${chronologyId}/tags`
      : null,
    (url: string) => backendClient.get(url).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  return {
    tagGroups: data?.tag_groups || [],
    tags: data?.tags || [],
    isLoading,
    isError: error,
    mutate,
  };
}

export function useChronologyConfig(matterGuid: string | undefined, chronologyId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<{ config: Record<string, unknown> }>(
    matterGuid && chronologyId
      ? `/matters/${matterGuid}/chronologies/${chronologyId}`
      : null,
    (url: string) => backendClient.get(url).then((r) => ({ config: r.data?.config || {} })),
    { revalidateOnFocus: false },
  );
  return {
    config: data?.config || {},
    isLoading,
    isError: error,
    mutate,
  };
}


export type { ChronologyTagGroupInfo, ChronologyTagInfo } from '@/hooks/useChronologies';

// ---------------------------------------------------------------------------
// Hooks: versions + history + restore
// ---------------------------------------------------------------------------

export function useVersions(matterGuid: string | undefined, chronologyId: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<VersionsResponse>(
    matterGuid && chronologyId
      ? `/matters/${matterGuid}/chronologies/${chronologyId}/versions`
      : null,
    (url: string) => backendClient.get(url).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  return {
    versions: data?.versions || [],
    reconstructionFloor: data?.reconstruction_floor?.earliest_reconstructable_version_number ?? null,
    nextCursor: data?.next_cursor || null,
    isLoading,
    isError: error,
    mutate,
  };
}

export function useEventHistory(matterGuid: string | undefined, eventGuid: string | undefined) {
  const url = matterGuid && eventGuid ? eventHistoryKey(matterGuid, eventGuid) : null;
  const { data, error, isLoading, mutate } = useSWR<EventHistoryResponse>(
    url,
    (u: string) => backendClient.get(u).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  return {
    history: data?.versions || [],
    isLoading,
    isError: error,
    mutate,
  };
}

export function useRestore(matterGuid: string | undefined, chronologyId: string | undefined) {
  const dryRun = useCallback(
    async (versionId: string): Promise<RestoreDryRunResponse> => {
      if (!matterGuid || !chronologyId) throw new Error('matterGuid and chronologyId required');
      const response = await backendClient.post(
        `/matters/${matterGuid}/chronologies/${chronologyId}/restorations`,
        { version_id: versionId, dry_run: true },
      );
      return response.data;
    },
    [matterGuid, chronologyId],
  );

  const execute = useCallback(async (versionId: string) => {
    if (!matterGuid || !chronologyId) throw new Error('matterGuid and chronologyId required');
    const response = await backendClient.post(
      `/matters/${matterGuid}/chronologies/${chronologyId}/restorations`,
      { version_id: versionId },
    );
    await revalidateChronologyData(matterGuid, chronologyId);
    return response.data as {
      version_id: string;
      summary: string | null;
      affected_event_ids: string[];
    };
  }, [matterGuid, chronologyId]);

  return { dryRun, execute };
}

// ---------------------------------------------------------------------------
// Document search (autocomplete picker for the Sources field)
// ---------------------------------------------------------------------------

export async function searchDocuments(
  matterGuid: string,
  q: string,
): Promise<DocumentSearchResult[]> {
  if (!q || q.length < 2) return [];
  const response = await backendClient.get(
    `/matters/${matterGuid}/documents/chronology-source-search`,
    { params: { q } },
  );
  return response.data?.results || [];
}
